import {
	CapTableColumn,
	StaticColumn,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/capTableTypes';
import { CopyCellText as CopyText } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/CopyCellText';
import {
	isCapTableRowSelected,
	IssuerNameCell,
	MipNameCell,
	SecurityNameCell,
	SecurityTypeNameCell,
	TableHeaderCell,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableElements';
import {
	CAP_TABLE_EXPAND_ROWS_MODE,
	CAP_TABLE_EXPAND_ROWS_MODE_OPTIONS,
	CAP_TABLE_VALUES_HELD_TYPE,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableSettings';
import { getCellValue } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableUtils';
import { CycleThroughMenu } from '@dealstack/frontend/shared/components/CycleThroughMenu/CycleThroughMenu';
import { RowIdentificationKey } from '@dealstack/shared/lib/common/business/cap/capTable.types';
import { checkIsNaN } from '@dealstack/shared/lib/checkIsNaN';
import { UndefinedItalic } from '@dealstack/frontend/shared/other/react/specialSigns';
import { Flex } from '@mantine/core';

export const getStaticColumns = ({
	showRowsCheckboxes,
	showSummarized,
	showPercentages,
	valuesHeldType,
	expandRowsMode,
	setExpandRowsMode,
}: {
	showRowsCheckboxes: boolean;
	showSummarized: boolean;
	showPercentages: boolean;
	valuesHeldType: CAP_TABLE_VALUES_HELD_TYPE;
	expandRowsMode: CAP_TABLE_EXPAND_ROWS_MODE;
	setExpandRowsMode: (value: CAP_TABLE_EXPAND_ROWS_MODE) => void;
}): CapTableColumn[] => {
	return [
		{
			id: StaticColumn.INSTRUMENT,
			meta: { className: StaticColumn.INSTRUMENT },
			header: () => (
				<TableHeaderCell className={StaticColumn.INSTRUMENT}>
					<Flex justify="space-between">
						<div>Instrument</div>
						<CycleThroughMenu
							items={CAP_TABLE_EXPAND_ROWS_MODE_OPTIONS}
							selectedValue={expandRowsMode}
							onChange={(value) => {
								setExpandRowsMode(value as CAP_TABLE_EXPAND_ROWS_MODE);
							}}
						/>
					</Flex>
				</TableHeaderCell>
			),
			cell: ({ row }) => {
				const rowData = row.original;
				if (RowIdentificationKey.issuersHoldings in rowData) {
					return (
						<MipNameCell
							row={row}
							name={rowData.mipName}
							isExpanded={row.getIsExpanded()}
						/>
					);
				}
				if (RowIdentificationKey.issuerSummarizedHolders in rowData) {
					return (
						<IssuerNameCell
							row={row}
							tags={rowData.issuerTags}
							name={rowData.issuerName}
							id={rowData.issuerId}
							isExpanded={row.getIsExpanded()}
						/>
					);
				}
				if (RowIdentificationKey.securityTypeSummarizedHolders in rowData) {
					return (
						<SecurityTypeNameCell
							row={row}
							type={rowData.securityType}
							showCheckbox={showRowsCheckboxes}
							isExpanded={row.getIsExpanded()}
						/>
					);
				}
				if (RowIdentificationKey.securityHolders in rowData) {
					return (
						<SecurityNameCell
							row={row}
							name={rowData.securityName}
							color={row.original.color}
							showCheckbox={showRowsCheckboxes}
						/>
					);
				}
				return;
			},
		},
		{
			id: StaticColumn.TOTAL_SECURITIES,
			meta: { className: StaticColumn.TOTAL_SECURITIES },
			header: () => (
				<TableHeaderCell className={StaticColumn.TOTAL_SECURITIES}>
					Total securities
				</TableHeaderCell>
			),
			cell: ({ table, row }) => {
				const rowData = row.original;
				const isRowExpanded = row.getIsExpanded();
				const isRowSelected = isCapTableRowSelected(table, row);

				const isEntityRow = RowIdentificationKey.issuerSummarizedHolders in rowData;
				if (isEntityRow) {
					if (isRowExpanded && !showPercentages) {
						return;
					}
					const totals = rowData.totals.strict;
					const value = getCellValue({
						holderValues: {
							amount: totals.totalAmount,
							totalCost: totals.totalCost,
							votes: totals.totalVotes,
							amountPercent: totals.totalAmount ? 100 : undefined,
							votesPercent: totals.totalVotes ? 100 : undefined,
							totalCostPercent: totals.totalCost ? 100 : undefined,
						},
						valuesHeldType,
						showSummarized,
						showPercentages,
					});
					if (checkIsNaN(value)) {
						return UndefinedItalic;
					}
					if (!value) {
						return;
					}
					return <CopyText text={value}>{value}</CopyText>;
				}

				const isTypeRow = RowIdentificationKey.securityTypeSummarizedHolders in rowData;
				if (isTypeRow) {
					if (isRowExpanded && !showPercentages) {
						return;
					}
					const totals = rowData.totals.strict;
					const value = getCellValue({
						holderValues: {
							amount: totals.totalAmount,
							totalCost: totals.totalCost,
							votes: totals.totalVotes,
							amountPercent: totals.totalAmount ? 100 : undefined,
							votesPercent: totals.totalVotes ? 100 : undefined,
							totalCostPercent: totals.totalCost ? 100 : undefined,
						},
						currency: rowData.currency,
						securityIssuedIn: rowData.securityIssuedIn,
						valuesHeldType,
						showSummarized,
						showPercentages,
					});
					if (checkIsNaN(value)) {
						return UndefinedItalic;
					}
					if (!value) {
						return;
					}
					return <CopyText text={value}>{isRowSelected ? value : ''}</CopyText>;
				}

				const isSecurityRow = RowIdentificationKey.securityHolders in rowData;
				if (isSecurityRow) {
					const totals = rowData.totals.strict;
					const value = getCellValue({
						holderValues: {
							amount: totals.totalAmount,
							totalCost: totals.totalCost,
							votes: totals.totalVotes,
							votesPercent: 100,
							amountPercent: 100,
							totalCostPercent: 100,
						},
						currency: rowData.currency,
						valuesHeldType,
						showSummarized,
						securityIssuedIn: rowData.securityIssuedIn,
						showPercentages,
					});
					if (checkIsNaN(value)) {
						return UndefinedItalic;
					}
					if (!value) {
						return;
					}
					return <CopyText text={value}>{isRowSelected ? value : ''}</CopyText>;
				}
				return;
			},
		},
	];
};
